import { forwardRef, useMemo } from 'react';

import type { DatePickerType, DatePickerValue, DatesRangeValue } from '@mantine/dates';
import {
  DatePickerInput as MantineDatePickerInput,
  DatePickerInputProps as MantineDatePickerInputProps,
} from '@mantine/dates';
import { IconCalendar, IconChevronLeft, IconChevronRight } from 'ui-icons';

import classes from './DatePickerInput.module.css';

type DatePickerInputProps<T extends DatePickerType = 'default'> = MantineDatePickerInputProps<T>;

const DatePickerInputBaseProps = {
  leftSection: <IconCalendar />,
  placeholder: 'Choose Date',
  rightSectionWidth: 32,
  weekdayFormat: 'ddd',
  hideOutsideDates: true,
  withCellSpacing: false,
  radius: 'sm',
  previousIcon: <IconChevronLeft />,
  nextIcon: <IconChevronRight />,
};

const DatePickerInputDefaultTypeProps: Partial<DatePickerInputProps<'default'>> = {
  allowDeselect: true,
};

const DatePickerInputRangeTypeProps: Partial<DatePickerInputProps<'range'>> = {
  placeholder: 'Start Date - End Date',
  allowSingleDateInRange: true,
};

const DatePickerInputDefaults = MantineDatePickerInput.extend({
  defaultProps: DatePickerInputBaseProps,
  classNames: {
    input: classes.input,
    placeholder: classes.placeholder,
    levelsGroup: classes.levelsGroup,
    calendarHeader: classes.calendarHeader,
    calendarHeaderControl: classes.calendarHeaderControl,
    calendarHeaderControlIcon: classes.calendarHeaderControlIcon,
    calendarHeaderLevel: classes.calendarHeaderLevel,
    weekday: classes.weekday,
    day: classes.day,
  },
});

const DatePickerInput = forwardRef(
  <T extends DatePickerType = 'default'>(props: DatePickerInputProps<T>) => {
    const { type } = props;

    const typeProps = useMemo(() => {
      if (type === 'default') {
        return DatePickerInputDefaultTypeProps;
      } else if (type === 'range') {
        return DatePickerInputRangeTypeProps;
      } else {
        return {};
      }
    }, [type]);

    return (
      <MantineDatePickerInput {...(typeProps as Partial<DatePickerInputProps<T>>)} {...props} />
    );
  }
);
// If we need to add additional props this component, we'll need to redefine the output type

DatePickerInput.displayName = 'DatePickerInput';

export { DatePickerInput, DatePickerInputDefaults };

export type { DatePickerInputProps, DatePickerValue, DatesRangeValue };
